import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useSipContext } from "@/context/SipContext";
import { useSoftphoneContext } from "@/context/SoftphoneContext";
import { Button } from "@/components/ui/button";
import { Phone, PhoneOff, Bell, Mic, MicOff, Pause, Play, Copy, Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { useLastCallStore } from "@/stores/lastCallStore";
import { formatPhoneNumber } from "@/utils/phoneNumber";
import axiosInstance from "@/utils/axiosInstance";
import { useUserContext } from "@/context/UserContext";
import { getTime } from "date-fns";

interface IncomingCall {
  id: string;
  sessionId: string;
  callerNumber: string;
  callerName?: string;
}

interface OutgoingCall {
  id: string;
  sessionId: string;
  calledNumber: string;
  status: "connecting" | "ringing" | "trying";
}

interface ActiveCall {
  sessionId: string;
  status: string;
  startTime: number;
  callerNumber?: string;
  callerName?: string;
}

interface SipCallManagerProps {
  inline?: boolean; // For use inside softphone modal
}

// Shared state across all instances (module-level singleton)
const stateSubscribers: Set<() => void> = new Set();

// Module-level shared state that all instances read from
let sharedIncomingCalls: IncomingCall[] = [];
let sharedOutgoingCalls: OutgoingCall[] = [];
let sharedActiveCalls: ActiveCall[] = [];

// Helper to notify all subscribed instances to re-render
const notifyStateChange = () => {
  stateSubscribers.forEach((fn) => fn());
};

// Timer component that updates independently without re-rendering parent
const CallTimer: React.FC<{ startTime: number }> = ({ startTime }) => {
  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const duration = Math.floor((currentTime - startTime) / 1000);
  const minutes = Math.floor(duration / 60);
  const seconds = duration % 60;

  return <>{`${minutes}:${seconds.toString().padStart(2, "0")}`}</>;
};

export const SipCallManager: React.FC<SipCallManagerProps> = ({
  inline = false,
}) => {
  const location = useLocation();
  const isDispatchPage = location.pathname === "/";
  const { flowControl, unhold, answerCall, rejectCall, toggleMute, holdCall } = useSipContext();
  const { openSoftphone } = useSoftphoneContext();
  const { setLastCall } = useLastCallStore();
  const {decodedToken} = useUserContext()

  // Force re-render when shared state changes
  const [, forceUpdate] = useState({});
  const [isMuted, setIsMuted] = useState(false);
  const [isOnHold, setIsOnHold] = useState(false);
  const [isInfo, setInfo] = useState(false);

  // Read from shared module-level state
  const incomingCalls = sharedIncomingCalls;
  const outgoingCalls = sharedOutgoingCalls;
  const activeCalls = sharedActiveCalls;

  // Sync mute state with flowControl
  useEffect(() => {
    if (flowControl) {
      setIsMuted(flowControl.micMuted);
    }
  }, [flowControl?.micMuted, flowControl]);

  // Reset mute/hold state when calls change
  useEffect(() => {
    if (activeCalls.length === 0) {
      setIsMuted(false);
      setIsOnHold(false);
    }
  }, [activeCalls.length]);

  // Subscribe to shared state changes
  useEffect(() => {
    const updateFn = () => forceUpdate({});
    stateSubscribers.add(updateFn);
    return () => {
      stateSubscribers.delete(updateFn);
    };
  }, []);

  const handleAnswerCall = useCallback(
    (sessionId: string) => {
      if (!answerCall) {
        console.error("Cannot answer call: answerCall method is not available");
        return;
      }

      try {
        console.log("Answering call:", sessionId);

        // Always open softphone when answering a call
        console.log("Opening softphone modal...");
        openSoftphone();

        // Answer the call using context method
        console.log("Calling answerCall()...");
        answerCall(sessionId);
        console.log("Call answered successfully");
      } catch (error) {
        console.error("Error answering call:", error);
        toast.error("Failed to answer call");
      }
    },
    [answerCall, openSoftphone],
  );

  const handleRejectCall = useCallback((sessionId: string) => {
      if (!rejectCall) return;
      try {
        // Reject call using context method
        rejectCall(sessionId);
      } catch (error) {
        console.error("Error rejecting call:", error);
        toast.error("Failed to reject call");
      }
    },
    [rejectCall],
  );

  const handleEndCall = useCallback(
    async (sessionId: string) => {
      if (!flowControl) return;

      try {
        // Use flowControl directly for ending specific calls by sessionId
        const {activeCall} = flowControl
        
        const {local_identity, remote_identity, start_time, end_time, direction} = activeCall || {}
        const duration_seconds = Math.floor((getTime(new Date()) - getTime(start_time)) / 1000)

        if (isInfo) {
          await axiosInstance.post("/call-logs", {
            call_sid: sessionId,
            client_phone: remote_identity?.uri.user,
            is_info_call: isInfo,
            settings_id: decodedToken?.settings.id,
            started_at: start_time,
            ended_at: end_time,
            status: "COMPLETED",
            direction: direction === 'outgoing' ? 'OUTBOUND' : 'INBOUND',
            duration_seconds,
            extension_number: direction === 'outgoing' ? local_identity.uri.user : ''
          })
          setInfo(false)
        }
        flowControl.hungup(sessionId);

      } catch (error) {
        console.error("Error ending call:", error);
        toast.error("Failed to end call");
      }
    },
    [flowControl, isInfo],
  );

  // Initialize from flowControl.callsQueue if needed (only on first render)
  useEffect(() => {
    if (incomingCalls.length > 0) return;
    if (flowControl?.callsQueue) {
      const inQueneCalls: IncomingCall[] = flowControl?.callsQueue
        .filter((call) => call.direction == "incoming" && !call.start_time)
        .map((call) => {
          return {
            id: call.id,
            sessionId: call.id,
            callerNumber: call.remote_identity?.uri?.user || "Unknown",
            callerName: call.remote_identity?.display_name,
          };
        });

      const outQueneCalls: OutgoingCall[] = flowControl?.callsQueue
        .filter((call) => call.direction == "outgoing" && !call.start_time)
        .map((call) => {
          return {
            id: call.id,
            sessionId: call.id,
            calledNumber: call.remote_identity?.uri?.user || "Unknown",
            status: "connecting",
          };
        });

      const activeQueneCalls = flowControl.callsQueue
        .filter((call) => call.start_time)
        .map((call) => {
          return {
            sessionId: call.id,
            status: "active",
            startTime: call.start_time,
            callerNumber: call.remote_identity?.uri?.user || "Unknown",
            callerName: call.remote_identity?.display_name,
          };
        });

      sharedActiveCalls = activeQueneCalls;
      sharedIncomingCalls = inQueneCalls;
      sharedOutgoingCalls = outQueneCalls;
      notifyStateChange();
    }
  }, [flowControl, incomingCalls.length]);

  useEffect(() => {
    if (!flowControl) return;

    // Set up handlers for:
    // 1. Non-inline (overlay) instances on non-dispatch pages
    // 2. Inline instances ONLY on dispatch page (when overlay is hidden)
    const shouldSetupHandlers = !inline || (inline && isDispatchPage);

    if (!shouldSetupHandlers) {
      console.log(
        "SipCallManager: Skipping event handler setup (overlay will handle it)",
      );
      return;
    }
    // Set up call event handlers
    flowControl.onCallActionConnection = (type, payload, data) => {
      console.log(`Call Event: ${type}`, payload, data);

      switch (type) {
        case "incomingCall": {
          const incomingCall: IncomingCall = {
            id: payload.id,
            sessionId: payload.id,
            callerNumber: payload.remote_identity?.uri?.user || "Unknown",
            callerName: payload.remote_identity?.display_name,
          };

          sharedIncomingCalls = [...sharedIncomingCalls, incomingCall];
          notifyStateChange();

          // Library (CallsFlowControl) handles ringtone automatically

          // Auto-open softphone on incoming call when on dispatch page
          if (isDispatchPage) {
            openSoftphone();
          }

          // Show browser notification for incoming call
          if (
            "Notification" in window &&
            Notification.permission === "granted"
          ) {
            const notification = new Notification("Incoming Call", {
              body: `Call from ${incomingCall.callerName || formatPhoneNumber(incomingCall.callerNumber)}`,
              icon: "/favicon.ico",
              requireInteraction: true,
            });

            notification.onclick = () => {
              window.focus();
              openSoftphone();
              notification.close();
            };

            // Auto-close notification after 30 seconds
            setTimeout(() => notification.close(), 30000);
          } else if (
            "Notification" in window &&
            Notification.permission === "default"
          ) {
            // Request permission if not granted
            Notification.requestPermission();
          }

          break;
        }

        case "outgoingCall": {
          const outgoingCall: OutgoingCall = {
            id: payload.id,
            sessionId: payload.id,
            calledNumber:
              payload.remote_identity?.uri?.user ||
              data?.remote_identity?.uri?.user ||
              "Unknown",
            status: "connecting",
          };

          sharedOutgoingCalls = [...sharedOutgoingCalls, outgoingCall];
          notifyStateChange();
          break;
        }

        case "callProgress": {
          // Update outgoing call status to ringing
          sharedOutgoingCalls = sharedOutgoingCalls.map((call) =>
            call.sessionId === payload
              ? { ...call, status: "ringing" }
              : call,
          );
          notifyStateChange();


          // Update toast message
          toast.dismiss(payload);
          const ringingCall = sharedOutgoingCalls.find(
            (call) => call.sessionId === payload,
          );
          if (ringingCall) {
            toast(`Ringing ${ringingCall.calledNumber}...`, {
              id: payload,
              duration: 30000,
              action: {
                label: "Cancel",
                onClick: () => handleEndCall(payload),
              },
            });
          }
          break;
        }

        case "callAccepted": {
          // Get call info before removing from incoming calls
          const incomingCall = sharedIncomingCalls.find(
            (call) => call.sessionId === payload,
          );
          const outgoingCall = sharedOutgoingCalls.find(
            (call) => call.sessionId === payload,
          );

          // Remove from incoming/outgoing calls and add to active calls
          sharedIncomingCalls = sharedIncomingCalls.filter((call) => call.sessionId !== payload);
          sharedOutgoingCalls = sharedOutgoingCalls.filter((call) => call.sessionId !== payload);
          sharedActiveCalls = [
            ...sharedActiveCalls,
            {
              sessionId: payload,
              status: "active",
              startTime: Date.now(),
              callerNumber:
                incomingCall?.callerNumber || outgoingCall?.calledNumber,
              callerName: incomingCall?.callerName,
            },
          ];
          notifyStateChange();
          // Library handles stopping ringtone
          toast.dismiss(payload);
          break;
        }

        case "callEnded": {
          // Find the call that's ending to save it as last call
          const endedIncomingCall = sharedIncomingCalls.find((call) => call.sessionId === payload);
          const endedActiveCall = sharedActiveCalls.find((call) => call.sessionId === payload);

          // Save the ended call as last call (only if it was answered/active)
          if (endedActiveCall) {
            const duration = endedActiveCall.startTime ? Math.floor((Date.now() - endedActiveCall.startTime) / 1000) : undefined;

            setLastCall({
              id: endedActiveCall.sessionId,
              number: endedActiveCall.callerNumber || 'Unknown',
              callerName: endedActiveCall.callerName,
              direction: endedIncomingCall ? 'incoming' : 'outgoing',
              status: 'answered',
              time: new Date().toISOString(),
              duration,
            });
          }

          // Remove from all call lists
          sharedIncomingCalls = sharedIncomingCalls.filter((call) => call.sessionId !== payload);
          sharedOutgoingCalls = sharedOutgoingCalls.filter((call) => call.sessionId !== payload);
          sharedActiveCalls = sharedActiveCalls.filter((call) => call.sessionId !== payload);
          notifyStateChange();
          // Library handles stopping ringtone
          toast.dismiss(payload);
          break;
        }

        case "callRejected":
          console.log('SipCallManager: Processing callRejected event for:', payload);
          console.log('Before filter - incoming calls:', sharedIncomingCalls.length);
          sharedIncomingCalls = sharedIncomingCalls.filter((call) => call.sessionId !== payload);
          sharedOutgoingCalls = sharedOutgoingCalls.filter((call) => call.sessionId !== payload);
          console.log('After filter - incoming calls:', sharedIncomingCalls.length);
          notifyStateChange();
          // Library handles stopping ringtone
          toast.dismiss(payload);
          console.log('SipCallManager: callRejected event processed successfully');
          break;

        case "hold":
          console.log("Call Event: hold - updating UI state");
          setIsOnHold(true);
          notifyStateChange();
          break;

        case "unhold":
          console.log("Call Event: unhold - updating UI state");
          setIsOnHold(false);
          notifyStateChange();
          break;

        case "mute":
          console.log("Call Event: mute - updating UI state");
          setIsMuted(true);
          notifyStateChange();
          break;

        case "unmute":
          console.log("Call Event: unmute - updating UI state");
          setIsMuted(false);
          notifyStateChange();
          break;

        default:
          break;
      }
    };

    // Cleanup function
    return () => {
      if (shouldSetupHandlers && flowControl) {
        console.log(
          `SipCallManager: Cleaning up event handlers for ${inline ? 'inline' : 'overlay'} instance`,
        );
        flowControl.onCallActionConnection = () => {};
      }
    };
  }, [
    flowControl,
    handleAnswerCall,
    handleEndCall,
    setLastCall,
    // Don't include outgoingCalls and incomingCalls as deps
    // They update frequently and cause handler re-registration
    // The handler uses module-level shared state instead
    inline,
    isDispatchPage,
    openSoftphone,
  ]);

  // For inline mode, show all calls (inside softphone modal)
  // For overlay mode (not inline):
  //   - On Dispatch page: don't show anything (softphone modal will handle it)
  //   - On other pages: show incoming calls as floating cards

  // Don't render overlay instance at all on Dispatch page
  // if (!inline && isDispatchPage) {
  //   return null;
  // }

  // const shouldRender =
  //   status.isConnected &&
  //   (incomingCalls.length || outgoingCalls.length || activeCalls.length);

  // if (!shouldRender) {
  //   return null;
  // }

  // In inline mode, show all calls
  // In overlay mode, show all calls (we're not on Dispatch page if we got here)
  // const shouldRenderIncomingCards = true;

  // Don't render overlay notifications on dispatch page - they'll be shown in softphone
  if (!inline && isDispatchPage) {
    return null;
  }

  return (
    <div
      className={
        inline ? "space-y-2" : "fixed top-4 right-4 z-[9999] space-y-2"
      }
    >
      {/* Incoming Calls - Only show in overlay mode if NOT on Dispatch page */}
      {
        incomingCalls.map((call) => (
          <Card
            key={call.id}
            className={`${inline ? "w-full" : "w-80"} border-2 border-green-500 bg-green-50 shadow-2xl`}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-green-800 flex items-center gap-2">
                <Bell className="h-4 w-4 animate-pulse" />
                Incoming Call
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="font-medium">
                  {call.callerName || formatPhoneNumber(call.callerNumber)}
                </p>
                {call.callerName && (
                  <p className="text-sm text-muted-foreground">
                    {formatPhoneNumber(call.callerNumber)}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => handleAnswerCall(call.sessionId)}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <Phone className="h-4 w-4 mr-1" />
                  Answer
                </Button>
                <Button
                  onClick={() => handleRejectCall(call.sessionId)}
                  variant="destructive"
                  className="flex-1"
                >
                  <PhoneOff className="h-4 w-4 mr-1" />
                  Decline
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

      {/* Outgoing Calls */}
      {outgoingCalls.map((call) => (
        <Card
          key={call.id}
          className={`${inline ? "w-full" : "w-80"} border-blue-500 bg-blue-50`}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-blue-800">
              {call.status === "connecting" && "Establishing Connection..."}
              {call.status === "ringing" && "Ringing..."}
              {call.status === "trying" && "Trying..."}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="font-medium">Calling {formatPhoneNumber(call.calledNumber)}</p>
              <p className="text-sm text-muted-foreground">
                Status:{" "}
                {call.status === "connecting"
                  ? "Connecting..."
                  : call.status === "ringing"
                    ? "Ringing..."
                    : "Trying..."}
              </p>
            </div>
            <Button
              onClick={() => handleEndCall(call.sessionId)}
              variant="destructive"
              className="w-full"
            >
              <PhoneOff className="h-4 w-4 mr-1" />
              Cancel Call
            </Button>
          </CardContent>
        </Card>
      ))}

      {/* Active Calls */}
      {activeCalls.map((call) => (
        <Card
          key={call.sessionId}
          className={`${inline ? "w-full" : "w-80"} border-blue-500 bg-blue-50`}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-blue-800 flex items-center justify-between">
              <span>Active Call</span>
              <span className="text-lg font-mono text-blue-900">
                <CallTimer startTime={call.startTime} />
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium">
                    {call.callerName || formatPhoneNumber(call.callerNumber) || "Unknown"}
                  </p>
                  {call.callerNumber && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => {
                        navigator.clipboard.writeText(formatPhoneNumber(call.callerNumber));
                        toast.success("Phone number copied!");
                      }}
                      title="Copy phone number"
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  Duration: <CallTimer startTime={call.startTime} />
                </p>
              </div>
              <div className="flex items-center gap-x-2">
                <Button
                  className="rounded-full w-10 h-10 p-0"
                  variant={isInfo ? "default" : "outline"}
                  title={`${isInfo ? "Unmark" : "Mark"} as informative call`}
                  onClick={() => setInfo(prev => !prev)}
                >
                  <Info className="w-5 h-5" />
                </Button>
                <Button
                  className="rounded-full w-10 h-10 p-0"
                  variant={isMuted ? "destructive" : "outline"}
                  onClick={() => {
                    if (toggleMute) {
                      toggleMute();
                    }
                  }}
                  title={isMuted ? "Unmute" : "Mute"}
                >
                  {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                </Button>
                <Button
                  className="rounded-full w-10 h-10 p-0"
                  variant={isOnHold ? "secondary" : "outline"}
                  onClick={() => {
                    if (isOnHold) {
                      // Unhold using context method
                      unhold();
                    } else {
                      // Hold using context method
                      if (holdCall) {
                        holdCall(call.sessionId);
                      }
                    }
                  }}
                  title={isOnHold ? "Resume" : "Hold"}
                >
                  {isOnHold ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
                </Button>
              </div>
            </div>
            <Button
              onClick={() => handleEndCall(call.sessionId)}
              variant="destructive"
              className="w-full"
            >
              <PhoneOff className="h-4 w-4 mr-1" />
              End Call
            </Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
